import ErrorBoundary from '@components/ErrorBoundary'
import Layout from '@components/Layout'
import { useAuth } from '@context/AuthContext'
import { usePageTracking } from '@hooks/usePageTracking'
import AccommodationDetailPage from '@pages/Accommodations/AccommodationDetailPage'
import AdminPage, { AdminRoute } from '@pages/Admin'
import AccommodationsPage from '@pages/Accommodations/AccommodationsPage'
import BalancesPage from '@pages/Balances/BalancesPage'
import ChatPage from '@pages/Chat/ChatPage'
import FilesPage from '@pages/Files'
import FlightDetailPage from '@pages/Flights/FlightDetailPage'
import FlightsPage from '@pages/Flights/FlightsPage'
import IdeasPage from '@pages/Ideas'
import IdeaDetailPage from '@pages/Ideas/IdeaDetailPage/IdeaDetailPage'
import HomePage from '@pages/Home'
import ItineraryImportPage from '@pages/ItineraryImport'
import EventCreatePage, { EVENT_CREATE_ROUTE } from '@pages/Itinerary/EventCreateFlow'
import EventEditPage, { EVENT_EDIT_ROUTE } from '@pages/Itinerary/EventEditFlow'
import ItineraryPage from '@pages/Itinerary/ItineraryPage'
import QuickEditPage from '@pages/Itinerary/QuickEdit/QuickEditPage'
import JoinTripPage from '@pages/JoinTrip'
import { JOIN_TRIP_PATH } from '@pages/JoinTrip/route'
import TripMapPage from '@pages/TripMap/TripMapPage'
import ProfilePage from '@pages/Profile'
import PublicTripOverviewPage from '@pages/PublicTripOverview/PublicTripOverviewPage'
import { PUBLIC_TRIP_OVERVIEW_PATH } from '@pages/PublicTripOverview/route'
import { PUBLIC_DETAIL_ROUTES, PUBLIC_SECTION_ROUTES } from '@pages/PublicSections/route'
import TripsPage from '@pages/Trips'
import CreateTripPage from '@pages/Trips/CreateTripFlow'
import EditTripDestinationsPage from '@pages/Trips/EditTripDestinationsFlow'
import MembersPage from '@pages/Trips/MembersPage'
import TripDetailPage from '@pages/Trips/TripDetailPage'
import React from 'react'
import { Toaster } from 'react-hot-toast'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import LoginPage from './pages/Login'
import { GlobalStyle } from './styles/GlobalStyle'
import { backgroundOrange, black, border1, danger, success, white } from '@/theme/colors'
import { fontBody } from '@/theme/fonts'
import { radii, shadows } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import PageLoader from '@components/PageLoader'

const AppContainer = styled.div`
	height: 100%;
	background: ${backgroundOrange};
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

/**
 * Page-scoped error boundary. Sits inside Layout so a crashed page leaves the
 * navigation usable, and is keyed by pathname so navigating away clears a
 * latched error instead of carrying it to the next page.
 */
const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const location = useLocation()
	return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { token, isLoading } = useAuth()

	// Show loading while checking authentication
	if (isLoading) {
		return (
			<PageLoader />
		)
	}

	if (!token) {
		return <Navigate to="/" replace />
	}

	return (
		<Layout>
			<RouteErrorBoundary>{children}</RouteErrorBoundary>
		</Layout>
	)
}

const RouteTracker: React.FC = () => {
	usePageTracking()
	return null
}

const App: React.FC = () => {
	const { token } = useAuth()

	return (
		<AppContainer>
			<GlobalStyle />
			<Toaster
				position="bottom-right"
				toastOptions={{
					duration: 3000,
					/* The warm surface, like a card: react-hot-toast takes its look as a style object. */
					style: {
						background: white,
						color: black,
						border: `1px solid ${border1}`,
						borderRadius: radii.inner,
						boxShadow: shadows.overlay,
						fontFamily: fontBody,
						fontSize: fontSizes.base,
					},
					success: {
						duration: 3000,
						iconTheme: {
							primary: success,
							secondary: white,
						},
					},
					error: {
						duration: 4000,
						iconTheme: {
							primary: danger,
							secondary: white,
						},
					},
				}}
			/>
			<Router>
				<RouteTracker />
				{/* Outer net: public pages (which render no Layout) and Layout itself.
				    Protected pages are caught by the inner boundary first, which
				    keeps the navigation on screen. */}
				<RouteErrorBoundary>
					<Routes>
						{/* Public Routes */}
						<Route path="/" element={token ? <Navigate to="/home" replace /> : <LoginPage />} />
						<Route path={PUBLIC_TRIP_OVERVIEW_PATH} element={<PublicTripOverviewPage />} />
						{/* The Join page: public, but serves signed-in visitors too — it is
					    where a Join link lands and where a typed code is previewed. */}
						<Route path={JOIN_TRIP_PATH} element={<JoinTripPage />} />
						{/* Per-section public detail pages, mounted straight from the
					    registry — registering a section IS mounting its route. */}
						{Object.entries(PUBLIC_SECTION_ROUTES).map(([section, { path, Component }]) => (
							<Route key={section} path={path} element={<Component />} />
						))}
						{/* Per-item read-only detail pages nested under a section list. */}
						{PUBLIC_DETAIL_ROUTES.map(({ key, path, Component }) => (
							<Route key={key} path={path} element={<Component />} />
						))}

						{/* Protected Routes */}
						<Route
							path="/home"
							element={
								<ProtectedRoute>
									<HomePage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/profile"
							element={
								<ProtectedRoute>
									<ProfilePage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips"
							element={
								<ProtectedRoute>
									<TripsPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/create"
							element={
								<ProtectedRoute>
									<CreateTripPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId"
							element={
								<ProtectedRoute>
									<TripDetailPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/members"
							element={
								<ProtectedRoute>
									<MembersPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/chat"
							element={
								<ProtectedRoute>
									<ChatPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/itinerary"
							element={
								<ProtectedRoute>
									<ItineraryPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path={EVENT_CREATE_ROUTE}
							element={
								<ProtectedRoute>
									<EventCreatePage />
								</ProtectedRoute>
							}
						/>
						<Route
							path={EVENT_EDIT_ROUTE}
							element={
								<ProtectedRoute>
									<EventEditPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/quick-edit"
							element={
								<ProtectedRoute>
									<QuickEditPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/destinations"
							element={
								<ProtectedRoute>
									<EditTripDestinationsPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/import"
							element={
								<ProtectedRoute>
									<ItineraryImportPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/map"
							element={
								<ProtectedRoute>
									<TripMapPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/accommodations"
							element={
								<ProtectedRoute>
									<AccommodationsPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/accommodations/:accommodationId"
							element={
								<ProtectedRoute>
									<AccommodationDetailPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/flights"
							element={
								<ProtectedRoute>
									<FlightsPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/flights/:flightGroupId"
							element={
								<ProtectedRoute>
									<FlightDetailPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/ideas"
							element={
								<ProtectedRoute>
									<IdeasPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/ideas/:ideaId"
							element={
								<ProtectedRoute>
									<IdeaDetailPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/files"
							element={
								<ProtectedRoute>
									<FilesPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/trips/:tripId/balances"
							element={
								<ProtectedRoute>
									<BalancesPage />
								</ProtectedRoute>
							}
						/>

						{/* Admin Routes (role-gated, own layout) */}
						<Route
							path="/admin/*"
							element={
								<AdminRoute>
									<AdminPage />
								</AdminRoute>
							}
						/>

						{/* Catch all route */}
						<Route path="*" element={<Navigate to="/" replace />} />
					</Routes>
				</RouteErrorBoundary>
			</Router>
		</AppContainer>
	)
}

export default App
